import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';

export function PasskeyManager() {
  const {
    passkeys,
    loadPasskeys,
    deletePasskey,
    register,
    isLoading,
    error,
    setError,
    isAuthenticated,
    user,
    recoveryCodesRemaining,
    generateRecoveryCodes,
    getRecoveryCodeCount,
  } = useAuth();

  const [showAddPasskey, setShowAddPasskey] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  const [newRecoveryCodes, setNewRecoveryCodes] = useState<string[] | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      loadPasskeys();
      getRecoveryCodeCount();
    }
  }, [isAuthenticated, loadPasskeys, getRecoveryCodeCount]);

  const handleAddPasskey = async () => {
    if (!user) return;
    try {
      const autoDeviceName = deviceName || detectDeviceName();
      await register(user.username, user.display_name, autoDeviceName);
      setShowAddPasskey(false);
      setDeviceName('');
      await loadPasskeys();
    } catch {
      // Error handled by hook
    }
  };

  const handleDeletePasskey = async (credentialId: string) => {
    const success = await deletePasskey(credentialId);
    if (success) {
      setConfirmDelete(null);
    }
  };

  const handleGenerateRecoveryCodes = async () => {
    const codes = await generateRecoveryCodes();
    if (codes) {
      setNewRecoveryCodes(codes);
      setShowRecoveryCodes(true);
    }
  };

  const detectDeviceName = () => {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) return 'iPhone';
    if (/iPad/.test(ua)) return 'iPad';
    if (/Mac/.test(ua)) return 'Mac';
    if (/Android/.test(ua)) return 'Android';
    if (/Windows/.test(ua)) return 'Windows PC';
    if (/Linux/.test(ua)) return 'Linux';
    return 'Unknown Device';
  };

  const getDeviceIcon = (deviceType: string | null) => {
    switch (deviceType) {
      case 'apple':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
          </svg>
        );
      case 'android':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85a.637.637 0 00-.83.22l-1.88 3.24a11.463 11.463 0 00-8.94 0L5.65 5.67a.643.643 0 00-.87-.2c-.28.18-.37.54-.22.83L6.4 9.48A10.78 10.78 0 001 18h22a10.78 10.78 0 00-5.4-8.52zM7 15.25a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5zm10 0a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5z"/>
          </svg>
        );
      case 'windows':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/>
          </svg>
        );
      case 'security_key':
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
          </svg>
        );
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="p-4 text-center text-text-soft">
        Please sign in to manage passkeys
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-text-primary">Passkey Management</h3>
        <button
          onClick={() => setShowAddPasskey(true)}
          className="flex items-center gap-2 px-3 py-1.5 bg-profit text-white rounded-lg text-sm hover:bg-profit/80"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Passkey
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-loss/20 rounded-lg text-loss text-sm">
          {error}
        </div>
      )}

      {/* Passkeys list */}
      <div className="space-y-3">
        {passkeys.length === 0 ? (
          <div className="p-4 text-center text-text-soft bg-background rounded-lg">
            No passkeys registered
          </div>
        ) : (
          passkeys.map((passkey) => (
            <div
              key={passkey.id}
              className="flex items-center justify-between p-4 bg-background rounded-lg border border-border"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center text-primary">
                  {getDeviceIcon(passkey.device_type)}
                </div>
                <div>
                  <div className="font-medium text-text-primary">
                    {passkey.device_name || 'Unknown Device'}
                  </div>
                  <div className="text-xs text-text-muted">
                    Added {new Date(passkey.created_at).toLocaleDateString()}
                    {passkey.last_used && (
                      <> &middot; Last used {new Date(passkey.last_used).toLocaleDateString()}</>
                    )}
                  </div>
                </div>
              </div>

              {confirmDelete === passkey.id ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDeletePasskey(passkey.id)}
                    disabled={isLoading}
                    className="px-3 py-1 bg-loss text-white rounded text-sm hover:bg-loss/80 disabled:opacity-50"
                  >
                    Confirm
                  </button>
                  <button
                    onClick={() => setConfirmDelete(null)}
                    className="px-3 py-1 bg-background border border-border rounded text-sm hover:bg-[#F8F6F1]"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmDelete(passkey.id)}
                  disabled={passkeys.length <= 1}
                  className="text-text-muted hover:text-loss transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={passkeys.length <= 1 ? 'Cannot delete last passkey' : 'Delete passkey'}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {/* Recovery codes section */}
      <div className="border-t border-border pt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-medium text-text-primary">Recovery Codes</h4>
            <p className="text-sm text-text-muted">
              {recoveryCodesRemaining > 0
                ? `${recoveryCodesRemaining} codes remaining`
                : 'No recovery codes generated'}
            </p>
          </div>
          <button
            onClick={handleGenerateRecoveryCodes}
            disabled={isLoading}
            className="px-3 py-1.5 bg-warning text-white rounded-lg text-sm hover:bg-warning/80 disabled:opacity-50"
          >
            {recoveryCodesRemaining > 0 ? 'Regenerate' : 'Generate'} Codes
          </button>
        </div>

        {recoveryCodesRemaining <= 3 && recoveryCodesRemaining > 0 && (
          <div className="p-3 bg-warning/20 rounded-lg text-warning text-sm">
            You're running low on recovery codes. Consider generating new ones.
          </div>
        )}
      </div>

      {/* Add passkey modal */}
      {showAddPasskey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#F8F6F1] rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl border border-border">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Add New Passkey</h3>

            <div className="mb-4">
              <label className="block text-sm text-text-soft mb-1">
                Device Name (optional)
              </label>
              <input
                type="text"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder={`Auto: ${detectDeviceName()}`}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-neutral"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAddPasskey(false);
                  setDeviceName('');
                  setError(null);
                }}
                className="flex-1 py-2 px-4 bg-background border border-border rounded-lg text-text-soft hover:bg-[#F8F6F1]"
              >
                Cancel
              </button>
              <button
                onClick={handleAddPasskey}
                disabled={isLoading}
                className="flex-1 py-2 px-4 bg-profit text-white rounded-lg hover:bg-profit/80 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Add Passkey'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recovery codes modal */}
      {showRecoveryCodes && newRecoveryCodes && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#F8F6F1] rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl border border-border">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-warning/20 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-primary">Recovery Codes</h3>
                <p className="text-sm text-text-soft">Save these codes securely</p>
              </div>
            </div>

            <div className="p-3 bg-warning/20 rounded-lg text-warning text-sm mb-4">
              These codes will only be shown once. Save them in a secure location.
            </div>

            <div className="grid grid-cols-2 gap-2 p-4 bg-background rounded-lg border border-border mb-4">
              {newRecoveryCodes.map((code, index) => (
                <code key={index} className="text-sm font-mono text-text-primary">
                  {code}
                </code>
              ))}
            </div>

            <button
              onClick={() => {
                setShowRecoveryCodes(false);
                setNewRecoveryCodes(null);
              }}
              className="w-full py-2 px-4 bg-primary text-white rounded-lg hover:bg-primary-muted"
            >
              I've Saved These Codes
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
